import { supabase, supabaseHelper, BusinessPlan as SupabasePlan } from '../../lib/supabase';
import { ExtendedBusinessPlan, SectionId, BusinessPlanSection } from '../../types/businessPlan';
import { BusinessPlanAdditionalData } from '../../components/business-plan/BusinessPlanWizard';

/**
 * Save a business plan to Supabase
 */
export const saveBusinessPlanToSupabase = async (plan: ExtendedBusinessPlan, isCustomWizard: boolean = false): Promise<ExtendedBusinessPlan> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('Not authenticated');
    }
    
    console.log('Saving business plan for authenticated user:', user.email);
    
    // Log the plan sections to debug missing content
    if (plan.sections) {
      console.log(`Plan ID: ${plan.id}, Status: ${plan.status}`);
      console.log(`Sections object has ${Object.keys(plan.sections).length} sections:`);
      
      // Track sections with large content that need to be stored separately
      const largeSections: {sectionId: string, content: string}[] = [];
      const CONTENT_SIZE_THRESHOLD = 10000; // Characters before we consider content "large"
      
      // Process sections to identify large content
      const processedSections = { ...plan.sections };
      
      // Log each section to track content
      Object.entries(plan.sections).forEach(([sectionId, section]) => {
        if (section && typeof section === 'object') {
          console.log(`Section ${sectionId}: status=${section.status}, content length=${section.content?.length || 0}`);
          
          // If content is large, mark it for separate storage
          if (section.content && section.content.length > CONTENT_SIZE_THRESHOLD) {
            console.log(`Large content detected for section ${sectionId}: ${section.content.length} chars`);
            largeSections.push({
              sectionId,
              content: section.content
            });
            
            // Replace content with a reference in the main plan
            processedSections[sectionId as SectionId] = {
              ...section,
              content: `ref:section_content:${plan.id}:${sectionId}`
            };
          }
        } else {
          console.warn(`Section ${sectionId} is not properly formatted:`, section);
        }
      });

      // Prepare the data for Supabase with processed sections
      const planData: SupabasePlan = {
        id: plan.id,
        user_id: user.id, // Always use the current authenticated user's ID
        idea_id: plan.idea_id,
        status: plan.status,
        content: plan.content || {},
        sections: processedSections, // Use processed sections with references
        version: plan.version || 1,
        updated_at: new Date().toISOString(),
        additional_data: isCustomWizard ? plan.additionalData : undefined,
        title: plan.title
      };

      // Save the main business plan
      const savedPlan = await supabaseHelper.createBusinessPlan(planData);

      if (!savedPlan) {
        throw new Error('Failed to save business plan to Supabase');
      }
      
      // Save any large sections to the separate table
      if (largeSections.length > 0) {
        console.log(`Saving ${largeSections.length} large sections to separate table`);
        
        const promises = largeSections.map(async ({ sectionId, content }) => {
          const { error } = await supabase
            .from('business_plan_section_content')
            .upsert({
              plan_id: plan.id,
              section_id: sectionId,
              content: content,
              updated_at: new Date().toISOString()
            });
          
          if (error) {
            console.error(`Error saving large content for section ${sectionId}:`, error);
            throw new Error(`Failed to save content for section ${sectionId}`);
          }
          
          return { sectionId, success: true };
        });
        
        // Wait for all large section saves to complete
        await Promise.all(promises);
      }
      
      // Restore the original content in the returned plan object (in memory only)
      // This ensures the frontend still has the complete content
      Object.entries(plan.sections).forEach(([sectionId, section]) => {
        if (section && typeof section === 'object' && section.content) {
          savedPlan.sections[sectionId] = {
            ...savedPlan.sections[sectionId],
            content: section.content
          };
        }
      });
      
      return savedPlan;
    } else {
      console.warn('No sections object in plan being saved');
    }

    // If no sections object, just save the plan as is
    const planData: SupabasePlan = {
      id: plan.id,
      user_id: user.id,
      idea_id: plan.idea_id,
      status: plan.status,
      content: plan.content || {},
      sections: plan.sections || {},
      version: plan.version || 1,
      updated_at: new Date().toISOString(),
      additional_data: isCustomWizard ? plan.additionalData : undefined,
      title: plan.title
    };

    const savedPlan = await supabaseHelper.createBusinessPlan(planData);

    if (!savedPlan) {
      throw new Error('Failed to save business plan to Supabase');
    }
    
    return savedPlan;
  } catch (error) {
    console.error('Error saving business plan to Supabase:', error);
    throw error;
  }
};

/**
 * Get a business plan by ID from Supabase
 */
export const getBusinessPlanFromSupabase = async (planId: string): Promise<ExtendedBusinessPlan | null> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('Not authenticated');
    }
    
    console.log('Getting business plan for authenticated user:', user.email);
    
    const { data: plan, error } = await supabase
      .from('business_plans')
      .select('*')
      .eq('id', planId)
      .eq('user_id', user.id)
      .single();
    
    if (error) {
      console.error('Error getting business plan:', error);
      return null;
    }
    
    if (!plan) {
      console.log('No business plan found');
      return null;
    }
    
    // Process sections to see if any need content from the separate table
    if (plan.sections) {
      const contentReferences: { sectionId: string }[] = [];
      
      // Scan for content references
      Object.entries(plan.sections).forEach(([sectionId, section]) => {
        if (section && typeof section === 'object' && 
            'content' in section && typeof section.content === 'string' && 
            section.content.startsWith('ref:section_content:')) {
          contentReferences.push({ sectionId });
        }
      });
      
      // If we have content to fetch from the separate table
      if (contentReferences.length > 0) {
        console.log(`Found ${contentReferences.length} sections with external content`);
        
        // Get all the large content items for this plan
        const { data: largeContents, error: contentError } = await supabase
          .from('business_plan_section_content')
          .select('*')
          .eq('plan_id', planId);
        
        if (contentError) {
          console.error('Error fetching large section content:', contentError);
        } else if (largeContents && largeContents.length > 0) {
          console.log(`Retrieved ${largeContents.length} large content items`);
          
          // Create a map for quick lookup
          const contentMap = largeContents.reduce((map, item) => {
            map[item.section_id] = item.content;
            return map;
          }, {} as Record<string, string>);
          
          // Restore content to the plan object
          contentReferences.forEach(({ sectionId }) => {
            const section = plan.sections[sectionId] as any;
            if (contentMap[sectionId] && section) {
              section.content = contentMap[sectionId];
              console.log(`Restored content for section ${sectionId}, length: ${contentMap[sectionId].length}`);
            } else {
              console.warn(`Missing large content for section ${sectionId}`);
            }
          });
        }
      }
    }
    
    // Transform the data to our application format
    const resultPlan = {
      ...plan,
      sections: plan.sections as Record<SectionId, BusinessPlanSection>,
      additionalData: plan.additional_data as BusinessPlanAdditionalData,
      title: plan.title || 'Untitled Business Plan'
    } as ExtendedBusinessPlan;
    
    return resultPlan;
  } catch (error) {
    console.error('Error in getBusinessPlanFromSupabase:', error);
    return null;
  }
};

/**
 * Get a business plan by idea ID from Supabase
 */
export const getBusinessPlanByIdeaIdFromSupabase = async (ideaId: string): Promise<ExtendedBusinessPlan | null> => {
  try {
    // Use the existing helper function
    const plan = await supabaseHelper.getBusinessPlan(ideaId);
    
    if (!plan) return null;

    // Transform the data to our application format
    return {
      ...plan,
      sections: plan.sections as Record<SectionId, BusinessPlanSection>,
      additionalData: plan.additional_data as BusinessPlanAdditionalData,
      title: plan.title || 'Untitled Business Plan'
    } as ExtendedBusinessPlan;
  } catch (error) {
    console.error('Error in getBusinessPlanByIdeaIdFromSupabase:', error);
    throw error;
  }
};

/**
 * Get all business plans for a user from Supabase
 */
export const getUserBusinessPlansFromSupabase = async (): Promise<ExtendedBusinessPlan[]> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('Not authenticated');
    }
    
    console.log('Getting business plans for authenticated user:', user.email);
    
    // First get all the business plans (metadata only initially)
    const { data: plans, error } = await supabase
      .from('business_plans')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    
    if (error) {
      console.error('Error getting business plans:', error);
      return [];
    }
    
    if (!plans || plans.length === 0) {
      console.log('No business plans found');
      return [];
    }
    
    console.log(`Found ${plans.length} business plans`);
    
    // Process to see if any have references to large content
    const plansNeedingLargeContent = [];
    const sectionReferences: {planId: string, sectionId: string}[] = [];
    
    // Check each plan for content references
    for (const plan of plans) {
      let needsContentFetch = false;
      
      if (plan.sections) {
        // Scan for content references
        Object.entries(plan.sections).forEach(([sectionId, section]) => {
          if (section && typeof section === 'object' && 
              'content' in section && typeof section.content === 'string' && 
              section.content.startsWith('ref:section_content:')) {
            sectionReferences.push({ planId: plan.id, sectionId });
            needsContentFetch = true;
          }
        });
      }
      
      if (needsContentFetch) {
        plansNeedingLargeContent.push(plan.id);
      }
    }
    
    // If we have plans needing large content
    if (plansNeedingLargeContent.length > 0) {
      console.log(`Found ${plansNeedingLargeContent.length} plans with external content`);
      
      // Get all the large content items for these plans
      const { data: largeContents, error: contentError } = await supabase
        .from('business_plan_section_content')
        .select('*')
        .in('plan_id', plansNeedingLargeContent);
      
      if (contentError) {
        console.error('Error fetching large section content:', contentError);
      } else if (largeContents && largeContents.length > 0) {
        console.log(`Retrieved ${largeContents.length} large content items`);
        
        // Create a map for quick lookup
        const contentMap: Record<string, Record<string, string>> = {};
        
        for (const item of largeContents) {
          if (!contentMap[item.plan_id]) {
            contentMap[item.plan_id] = {};
          }
          contentMap[item.plan_id][item.section_id] = item.content;
        }
        
        // Restore content to the plan objects
        for (const { planId, sectionId } of sectionReferences) {
          const plan = plans.find(p => p.id === planId);
          if (plan && contentMap[planId] && contentMap[planId][sectionId]) {
            const section = plan.sections[sectionId] as any;
            if (section) {
              section.content = contentMap[planId][sectionId];
              console.log(`Restored content for plan ${planId}, section ${sectionId}`);
            }
          }
        }
      }
    }
    
    // Transform the data to our application format
    return plans.map(plan => ({
      ...plan,
      sections: plan.sections as Record<SectionId, BusinessPlanSection>,
      additionalData: plan.additional_data as BusinessPlanAdditionalData,
      title: plan.title || 'Untitled Business Plan'
    })) as ExtendedBusinessPlan[];
    
  } catch (error) {
    console.error('Error in getUserBusinessPlansFromSupabase:', error);
    return [];
  }
};

/**
 * Delete a business plan from Supabase
 */
export const deleteBusinessPlanFromSupabase = async (planId: string): Promise<void> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('Not authenticated');
    }
    
    console.log('Deleting business plan for authenticated user:', user.email, 'Plan ID:', planId);

    // First, verify that the plan exists
    const { data: plan, error: fetchError } = await supabase
      .from('business_plans')
      .select('user_id, id')
      .eq('id', planId)
      .single();

    if (fetchError) {
      console.error('Error fetching business plan from Supabase:', fetchError);
      throw fetchError;
    }

    if (!plan) {
      console.error('Business plan not found with ID:', planId);
      throw new Error('Business plan not found');
    }

    console.log('Found business plan to delete:', plan);

    if (plan.user_id !== user.id) {
      console.error('Unauthorized: Plan does not belong to the current user');
      throw new Error('Unauthorized: You can only delete your own business plans');
    }

    // Delete the business plan with explicit logging
    console.log('Executing delete query for plan ID:', planId);
    const { error, count } = await supabase
      .from('business_plans')
      .delete({ count: 'exact' })
      .eq('id', planId);

    if (error) {
      console.error('Error deleting business plan from Supabase:', error);
      throw error;
    }

    console.log(`Successfully deleted ${count} business plan(s) with ID:`, planId);
    
    // Invalidate any cached data by setting a flag in localStorage
    localStorage.setItem('business_plans_cache_invalidated', 'true');
    localStorage.setItem('business_plans_cache_invalidated_timestamp', Date.now().toString());
    
    // Also remove the plan from the cached plans if it exists
    const cachedPlansJson = localStorage.getItem('cached_business_plans');
    if (cachedPlansJson) {
      try {
        const cachedPlans = JSON.parse(cachedPlansJson);
        const updatedPlans = cachedPlans.filter((plan: any) => plan.id !== planId);
        localStorage.setItem('cached_business_plans', JSON.stringify(updatedPlans));
        console.log('Updated cached plans, removed plan ID:', planId);
      } catch (e) {
        console.error('Error updating cached plans:', e);
        // If there's an error parsing or updating, just remove the cache entirely
        localStorage.removeItem('cached_business_plans');
      }
    }
  } catch (error) {
    console.error('Error in deleteBusinessPlanFromSupabase:', error);
    throw error;
  }
};

/**
 * Delete all business plans for the current user
 */
export const deleteAllBusinessPlansForCurrentUser = async (): Promise<{ count: number }> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      throw new Error('Not authenticated');
    }
    
    console.log('Attempting to delete ALL business plans for user:', user.email);

    // Delete all business plans for this user
    const { data, error, count } = await supabase
      .from('business_plans')
      .delete({ count: 'exact' })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting all business plans:', error);
      throw error;
    }

    console.log(`Successfully deleted ${count} business plan(s) for user ${user.email}`);
    
    // Invalidate any cached data
    localStorage.setItem('business_plans_cache_invalidated', 'true');
    localStorage.setItem('business_plans_cache_invalidated_timestamp', Date.now().toString());
    localStorage.removeItem('cached_business_plans');
    
    return { count: count || 0 };
  } catch (error) {
    console.error('Error in deleteAllBusinessPlansForCurrentUser:', error);
    throw error;
  }
};

/**
 * Delete all business plans for the current user from Supabase
 */
export const deleteAllBusinessPlansFromSupabase = async (): Promise<void> => {
  try {
    // Use our new implementation that returns the count
    const { count } = await deleteAllBusinessPlansForCurrentUser();
    console.log(`Deleted ${count} business plans from Supabase`);
  } catch (error) {
    console.error('Error in deleteAllBusinessPlansFromSupabase:', error);
    throw error;
  }
};
